import { actionTypes as profileActionTypes } from '~/profile-sidebar/reducer';
import {
  actions as dataFetchActions,
  actionTypes as dataFetchActionTypes,
} from '@bufferapp/async-data-fetch';
import { actions as notificationActions } from '@bufferapp/notifications';
import { campaignTab } from '~/routes';
import { CampaignTab } from '~/campaign/types';
import { PostActionNotificationSuccess } from '~/post/types';
import { emptyQueue } from '~/general-settings/thunks/emptyQueue';
import { cleanQueue } from '~/general-settings/thunks/cleanQueue';
import { actions, actionTypes } from './reducer';

export default ({ dispatch, getState }) =>
  (next) =>
  (action) => {
    next(action);
    switch (action.type) {
      case profileActionTypes.SELECT_PROFILE: {
        dispatch(
          dataFetchActions.fetch({
            name: 'queuedPosts',
            args: {
              profileId: action.profile.id,
              isFetchingMore: false,
              count: 300,
            },
          })
        );
        break;
      }
      case `updateSchedule_${dataFetchActionTypes.FETCH_SUCCESS}`:
      case `updatePausedSchedules_${dataFetchActionTypes.FETCH_SUCCESS}`:
      case `toggleInstagramReminders_${dataFetchActionTypes.FETCH_SUCCESS}`:
      case `${cleanQueue.fulfilled}`:
      case `${emptyQueue.fulfilled}`: {
        // depeding on the action the profileId is at a different location
        const profileId = action.args?.profileId || action.meta?.arg?.profileId;

        dispatch(
          dataFetchActions.fetch({
            name: 'queuedPosts',
            args: {
              profileId,
              isFetchingMore: false,
              count: 300,
            },
          })
        );
        break;
      }
      case 'COMPOSER_EVENT':
        // eslint-disable-next-line no-case-declarations
        const fromIdea = action.data?.fromIdea;
        if (action.eventType === 'saved-drafts' && !fromIdea) {
          dispatch(
            notificationActions.createNotification({
              notificationType: 'success',
              message: action.data.message,
            })
          );
        }
        break;
      case `requeuePost_${dataFetchActionTypes.FETCH_SUCCESS}`:
        dispatch(
          dataFetchActions.fetch({
            name: 'queuedPosts',
            args: {
              profileId: action.args.profileId,
              isFetchingMore: false,
              isReordering: true,
              count: 300,
            },
          })
        );
        break;
      case `queuedPosts_${dataFetchActionTypes.FETCH_SUCCESS}`:
        if (action.args.isReordering) {
          dispatch(
            notificationActions.createNotification({
              notificationType: 'success',
              message: "We've re-added this post to your queue!",
            })
          );
        }
        break;
      case `deletePost_${dataFetchActionTypes.FETCH_SUCCESS}`: {
        const label = action.args?.isDraft ? 'draft' : 'post';
        dispatch(
          notificationActions.createNotification({
            notificationType: 'success',
            message: `Okay, we've deleted that ${label}!`,
          })
        );
        /**
         * We also re-fetch the queue when a post is deleted
         * as this may have caused posts to change schedule
         */
        dispatch(
          dataFetchActions.fetch({
            name: 'queuedPosts',
            args: {
              profileId: action.args.profileId,
              isFetchingMore: false,
              hideLoading: true,
              count: 300,
            },
          })
        );
        break;
      }
      case actionTypes.POST_CONFIRMED_DELETE:
        dispatch(
          dataFetchActions.fetch({
            name: 'deletePost',
            args: {
              updateId: action.updateId,
              profileId: action.profileId,
            },
          })
        );
        break;
      case actionTypes.POST_SHARE_NOW:
        dispatch(
          dataFetchActions.fetch({
            name: 'sharePostNow',
            args: {
              updateId: action.post.id,
              profileId: action.profileId,
              profileService: action.post.profile_service,
            },
          })
        );
        break;
      case actionTypes.POST_REQUEUE:
        dispatch(
          dataFetchActions.fetch({
            name: 'requeuePost',
            args: {
              updateId: action.post.id,
              profileId: action.profileId,
            },
          })
        );
        break;
      case actionTypes.VIEW_CAMPAIGN_PAGE: {
        const { campaignId, isSent } = action;
        if (isSent) {
          dispatch(campaignTab.goTo({ campaignId, tab: CampaignTab.Sent }));
        } else {
          dispatch(
            campaignTab.goTo({ campaignId, tab: CampaignTab.Scheduled })
          );
        }
        break;
      }
      case `sharePostNow_${dataFetchActionTypes.FETCH_SUCCESS}`:
        dispatch(
          notificationActions.createNotification({
            notificationType: 'success',
            message: PostActionNotificationSuccess.SHARE_NOW,
          })
        );
        break;
      case `sharePostNow_${dataFetchActionTypes.FETCH_FAIL}`:
        dispatch(
          notificationActions.createNotification({
            notificationType: 'error',
            message: action.error,
          })
        );
        break;
      case actionTypes.POST_DROPPED: {
        dispatch(
          dataFetchActions.fetch({
            name: 'dropPost',
            args: {
              updateId: action.updateId,
              timestamp: action.timestamp,
            },
          })
        );
        break;
      }
      case actionTypes.POSTS_SWAPPED: {
        dispatch(
          dataFetchActions.fetch({
            name: 'swapPosts',
            args: {
              updateSourceId: action.postSource.id,
              sourcePinned: action.postSource.postProps.pinned,
              sourceDueAt: action.postTarget.postProps.due_at,

              updateTargetId: action.postTarget.id,
              targetPinned: action.postTarget.postProps.pinned,
              targetDueAt: action.postSource.postProps.due_at,
            },
          })
        );
        break;
      }

      /**
       * Watch for Pusher events to keep post counts up-to-date throughout the app.
       */
      case actionTypes.POST_CREATED:
      case actionTypes.POST_DELETED:
      case actionTypes.POST_SENT: {
        const state = getState();
        const { profileId } = action;
        const currentCounts = {
          pending: state.queue.byProfileId[profileId]?.total || 0,
        };
        const changeMap = {
          [actionTypes.POST_CREATED]: { pending: 1 },
          [actionTypes.POST_DELETED]: { pending: -1 },
          [actionTypes.POST_SENT]: { pending: -1 },
        };
        const countChanges = changeMap[action.type];
        const newCounts = {
          pending: currentCounts.pending + countChanges.pending,
        };
        dispatch(actions.postCountUpdated(profileId, newCounts));
        /**
         * We also re-fetch the queue
         */
        dispatch(
          dataFetchActions.fetch({
            name: 'queuedPosts',
            args: {
              profileId,
              isFetchingMore: false,
              hideLoading: true,
              count: 300,
            },
          })
        );
        break;
      }

      default:
        break;
    }
  };
